import React, {useReducer} from 'react'
import {
    getDefaultAbutmentsInfo,
    getDefaultAvailableToReturn,
    getDefaultClinicalCondition,
    getDefaultComplaintType,
    getDefaultGeneralComplaint,
    getDefaultGeneralInfo,
    getDefaultGuestInfo,
    getDefaultInstrumentInfo,
    getDefaultMetadata,
    getDefaultPatientInfo,
    getDefaultReplacements,
    getDefaultSecondaryDevice,
    getDefaultShippingInfo,
    getDefaultTreatmentDates
} from '../AppStateDefaults'
import {Action, ComplaintReducer, ComplaintState} from '../types'
import combineReducers from "react-combine-reducers";
import generalInfoReducer from "../GeneralInfoReducer";
import guestInfoReducer from "../GuestInfoReducer";
import generalComplaintReducer from "../GeneralComplaintReducer";
import treatmentDatesReducer from "../TreatmentDatesReducer";
import patientInfoReducer from "../PatientInfoReducer";
import complaintTypeReducer from "../ComplaintTypeReducer";
import clinicalConditionReducer from "../ClinicalConditionReducer";
import abutmentsInfoReducer from "../AbutmentsInfoReducer";
import instrumentInfoReducer from "../InstrumentInfoReducer";
import availableToReturnReducer from "../AvailableToReturnReducer";
import secondaryDeviceReducer from "../SecondaryDeviceReducer";
import replacementsReducer from "../ReplacementsReducer";
import shippingInfoReducer from "../ShippingInfoReducer";
import dateReviver from '../../lib/date-helpers'
import rootReducerFactory from "./RootReducerFactory";
import metadataReducer from "../MetadataReducer";

export const getComplaintFromSessionStorage = () => {
    const complaintData = sessionStorage.getItem('complaintData')

    return complaintData
        ? JSON.parse(complaintData, dateReviver) as ComplaintState
        : undefined
}

const initializeComplaint = (initialComplaint: ComplaintState) => getComplaintFromSessionStorage() || initialComplaint

export const useComplaintReducer = (): [ComplaintState, React.Dispatch<Action>] => {
    const [complaintReducer, initialComplaint] =
        combineReducers<ComplaintReducer>({
            generalInfo: [generalInfoReducer, getDefaultGeneralInfo()],
            guestInfo: [guestInfoReducer, getDefaultGuestInfo()],
            generalComplaint: [generalComplaintReducer, getDefaultGeneralComplaint()],
            treatmentDates: [treatmentDatesReducer, getDefaultTreatmentDates()],
            patientInfo: [patientInfoReducer, getDefaultPatientInfo()],
            complaintType: [complaintTypeReducer, getDefaultComplaintType()],
            clinicalCondition: [clinicalConditionReducer, getDefaultClinicalCondition()],
            abutmentsInfo: [abutmentsInfoReducer, getDefaultAbutmentsInfo()],
            instrumentInfo: [instrumentInfoReducer, getDefaultInstrumentInfo()],
            availableToReturn: [availableToReturnReducer, getDefaultAvailableToReturn()],
            secondaryDevice: [secondaryDeviceReducer, getDefaultSecondaryDevice()],
            replacements: [replacementsReducer, getDefaultReplacements()],
            shippingInfo: [shippingInfoReducer, getDefaultShippingInfo()],
            metadata: [metadataReducer, getDefaultMetadata()]
        })

    // A Root Reducer allows for a global state change, e.g., re-initializing the entire
    // state object upon form cancellation, when using combined reducers
    // https://stackoverflow.com/questions/35622588/how-to-reset-the-state-of-a-redux-store
    const rootReducer = rootReducerFactory(complaintReducer)

    // Acquire the state object and the dispatch method
    const [complaint, dispatch] = useReducer<ComplaintReducer>(
        rootReducer,
        initializeComplaint(initialComplaint)
    )

    return [complaint, dispatch]
}

export default useComplaintReducer
